import {Card, Spinner} from 'react-bootstrap'
import {useEffect, useState} from 'react'
import ApexCharts, {ApexOptions} from 'apexcharts'
import {useLazyQuery} from '@apollo/client'
import {GET_STATUS_POLICIES_DISTRIBUTION} from '../../../gql/queries/reportsQuery'
import {getBootstrapColorHex} from '../../../helpers/consts'

interface IStatusPoliciesDistribution {
  statusCode: string
  statusTitle: string
  statusColor: string
  policiesCount: number
  percentage: number
}

interface IResStatusPoliciesDistribution {
  statusPoliciesDistribution: IStatusPoliciesDistribution[]
}

interface IStatusPoliciesChartProps {
  dateFrom: string
  dateTo: string
  dateCriteria: 'submitted' | 'effective'
  carrier?: string
  stateEu?: string
  policyType?: string
  status?: string[]
  agent?: string[]
  loading?: boolean
}

export const StatusPoliciesChart = ({
  dateFrom,
  dateTo,
  dateCriteria,
  carrier,
  stateEu,
  policyType,
  status,
  agent,
  loading: parentLoading,
}: IStatusPoliciesChartProps) => {
  const [getDistributionData, {data, loading}] = useLazyQuery<IResStatusPoliciesDistribution>(
    GET_STATUS_POLICIES_DISTRIBUTION,
    {
      fetchPolicy: 'no-cache',
    }
  )

  useEffect(() => {
    getDistributionData({
      variables: {
        dateFrom,
        dateTo,
        dateCriteria,
        carrier: carrier ? [carrier] : undefined,
        stateEu: stateEu ? [stateEu] : undefined,
        policyType: policyType ? [policyType] : undefined,
        status: status,
        agent: agent,
        limit: 10,
      },
    })
  }, [dateFrom, dateTo, dateCriteria, carrier, stateEu, policyType, status, agent, getDistributionData])

  useEffect(() => {
    if (!data?.statusPoliciesDistribution || loading || parentLoading) return

    const distributionData = data.statusPoliciesDistribution

    const labels = distributionData.map((item) => item.statusTitle)
    const series = distributionData.map((item) => item.policiesCount)
    const percentages = distributionData.map((item) => item.percentage)
    const colors = distributionData.map((item) => getBootstrapColorHex(item.statusColor))

    const options: ApexOptions = {
      series,
      chart: {
        type: 'donut',
        height: 350,
      },
      labels,
      legend: {
        position: 'right',
        fontSize: '12px',
        markers: {
          width: 8,
          height: 8,
        },
        formatter: (seriesName, opts) => {
          const count = series[opts.seriesIndex]
          return `${seriesName} (${count})`
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val: number) => `${val.toFixed(1)}%`,
        style: {
          fontSize: '12px',
          fontWeight: 'bold',
        },
      },
      tooltip: {
        y: {
          formatter: (val, opts) => {
            const percentage = percentages[opts.seriesIndex]
            return `${val} policies (${percentage.toFixed(2)}%)`
          },
        },
      },
      colors,
      plotOptions: {
        pie: {
          donut: {
            size: '25%',
            labels: {
              show: false,
            },
          },
        },
      },
    }

    const chart = new ApexCharts(document.querySelector('#status-policies-distribution-chart'), options)
    chart.render()

    return () => {
      chart.destroy()
    }
  }, [data, loading, parentLoading])

  return (
    <Card className='card-flush h-100'>
      <Card.Header className='border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>Policies per status (Top 10)</span>
          <span className='text-muted fw-semibold fs-7'>
            What is the status of your policies
          </span>
        </h3>
      </Card.Header>
      <Card.Body className='pt-0'>
        {(loading || parentLoading) && (
          <div className='d-flex justify-content-center align-items-center' style={{minHeight: 350}}>
            <Spinner animation='border' />
          </div>
        )}
        {!loading && !parentLoading && (
          <div id='status-policies-distribution-chart' style={{minHeight: 350}}></div>
        )}
      </Card.Body>
    </Card>
  )
}
